import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {OrganizationConstant} from '@models/adminstration/organization/constants/organization-constant';
import {PortalGeneralSetting} from '@models/adminstration/organization/constants/portal-general-setting';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {OrganizationConstantValue} from '@models/adminstration/organization/constants/organization-constant-value';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConstantsService {
  private readonly BASE_URL: string;
  ORGANIZATION_CONSTANTS_LIST_URL_SUFFIX: string = '/api/web/admin/tenant/:id/constants';
  ORGANIZATION_CONSTANTS_LIST_VALID_NUMERIC_URL_SUFFIX: string = '/api/web/admin/tenant/:id/constants/validNumericConstants';
  ORGANIZATION_CONSTANTS_LIST_PORTAL_SETTINGS_URL_SUFFIX: string = '/api/web/admin/tenant/:id/constants/portalSettings';
  ORGANIZATION_CONSTANT_SAVE_URL_SUFFIX: string = '/api/web/admin/tenant/:id/constants/:constant';
  ORGANIZATION_CONSTANT_DELETE_URL_SUFFIX: string = '/api/web/admin/tenant/:id/constants/:constant';

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public listConstants(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_CONSTANTS_LIST_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));

    return this.http.get<OrganizationConstant[]>(url, HTTPS_REQUEST_OPTIONS).pipe(map(organizationConstants => {
      return (organizationConstants).map(item => {
        if (!item.tenantConstant.constantValues) {
          item.tenantConstant.constantValues = [];
        }
        return item;
      });
    }));
  }

  public listPortalSettings(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_CONSTANTS_LIST_PORTAL_SETTINGS_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));

    return this.http.get<PortalGeneralSetting[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public listValidNumericConstants(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_CONSTANTS_LIST_VALID_NUMERIC_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));

    return this.http.get<OrganizationConstant[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveConstant(organizationId: number, constant: OrganizationConstant): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_CONSTANT_SAVE_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));
    url = url.replace(':constant', String(constant.tenantConstant.id));

    return this.http.post<OrganizationConstant>(url, constant, HTTPS_REQUEST_OPTIONS);
  }

  public deleteConstant(organizationId: number, constantId: number): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_CONSTANT_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));
    url = url.replace(':constant', String(constantId));

    return this.http.delete<boolean>(url, HTTPS_REQUEST_OPTIONS);
  }

  public constantIntervalIsActive(value: OrganizationConstantValue): boolean {
    const current = new Date().getTime();
    const start = value.startTimestamp;
    const end = value.endTimestamp;

    return start <= current && end >= current;
  }

  public constantIntervalIsInPast(value: OrganizationConstantValue) {
    const current = new Date().getTime();
    const start = value.startTimestamp;
    const end = value.endTimestamp;

    return start < current && end < current;
  }

  public copyConstantProperties(source: OrganizationConstant, target: OrganizationConstant | null): OrganizationConstant {
    if (target == null) {
      target = new OrganizationConstant();
    }
    target.portalSetting.id = source.portalSetting.id;
    target.portalSetting.name = source.portalSetting.name;
    target.portalSetting.value = source.portalSetting.value;
    target.portalSetting.isPortalConstant = source.portalSetting.isPortalConstant;

    target.tenantConstant.id = source.tenantConstant.id;
    target.tenantConstant.parentId = source.tenantConstant.parentId;
    target.tenantConstant.settingId = source.tenantConstant.settingId;
    target.tenantConstant.unit = source.tenantConstant.unit;
    target.tenantConstant.decimalPlaces = source.tenantConstant.decimalPlaces;
    target.tenantConstant.used = source.tenantConstant.used;
    target.tenantConstant.constantValues = new Array<OrganizationConstantValue>();
    for (const item of source.tenantConstant.constantValues) {
      const newValue = new OrganizationConstantValue();
      newValue.value = item.value;
      newValue.startTimestamp = item.startTimestamp;
      newValue.endTimestamp = item.endTimestamp;
      target.tenantConstant.constantValues.push(newValue);
    }

    return target;

  }

  public verifyOverlappingIntervals(form: UntypedFormGroup): boolean {
    this.resetFormErrors(form);
    const valuesArray = form.controls.editValuesFormArray as UntypedFormArray;
    for (let i = 0; i < valuesArray.length; i++) {
      const compare: UntypedFormGroup = valuesArray.controls[i] as UntypedFormGroup;
      for (let j = 0; j < valuesArray.length; j++) {
        if (i == j) {
          continue;
        }
        const compareWith: UntypedFormGroup = valuesArray.controls[j] as UntypedFormGroup;
        if (this.areIntervalsOverlapping(compare, compareWith)) {
          compareWith.setErrors({overlapping: true});
          compare.setErrors({overlapping: true});
          return true;
        }

      }
    }
    return false;
  }

  private resetFormErrors(formGroup: UntypedFormGroup) {
    const valuesArray = formGroup.controls.editValuesFormArray as UntypedFormArray;
    for (let i = 0; i < valuesArray.length; i++) {
      const formToReset: UntypedFormGroup = valuesArray.controls[i] as UntypedFormGroup;
      formToReset.setErrors(null);
    }
  }

  private areIntervalsOverlapping(compare: UntypedFormGroup, compareWith: UntypedFormGroup): boolean {

    const startTimestamp1 = compare.controls.startTimestamp.value;
    const endTimestamp1 = compare.controls.endTimestamp.value;
    const startTimestamp2 = compareWith.controls.startTimestamp.value;
    const endTimestamp2 = compareWith.controls.endTimestamp.value;

    return (startTimestamp1 >= startTimestamp2 && startTimestamp1 < endTimestamp2) ||
        (endTimestamp1 >= startTimestamp2 && endTimestamp1 < endTimestamp2) ||
        (startTimestamp2 >= startTimestamp1 && startTimestamp2 < endTimestamp1) ||
        (endTimestamp2 >= startTimestamp1 && endTimestamp2 < endTimestamp1);

  }
}
